// src/components/panel/AnularOrdenes.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/panel/anularOrdenes.css";

// CRA/Webpack friendly
const API_BASE = process.env.REACT_APP_API_URL || "";

const anorFormatCRC = (n) => {
  const num = Number(n || 0);
  return "¢" + num.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const anorFormatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("es-CR");
};

const anorSafeText = (v, fallback = "N/A") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
};

const AnularOrdenes = () => {
  const navigate = useNavigate();

  // ✅ Guard
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) navigate("/login");
  }, [navigate]);

  // UI state
  const [anorMode, setAnorMode] = useState("number"); // number | date
  const [anorOrderNumber, setAnorOrderNumber] = useState("");
  const [anorDateFrom, setAnorDateFrom] = useState("");
  const [anorDateTo, setAnorDateTo] = useState("");

  const [anorLoading, setAnorLoading] = useState(false);
  const [anorOrders, setAnorOrders] = useState([]);

  // Modal detalle
  const [anorDetailOpen, setAnorDetailOpen] = useState(false);
  const [anorDetailLoading, setAnorDetailLoading] = useState(false);
  const [anorSelectedId, setAnorSelectedId] = useState(null);
  const [anorSelectedOrder, setAnorSelectedOrder] = useState(null);

  const [anorAnnulLoading, setAnorAnnulLoading] = useState(false);
  const [anorMsg, setAnorMsg] = useState(null); // {type:'ok'|'err', text:''}

  const anorToken = useMemo(() => localStorage.getItem("token"), []);
  const anorHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (anorToken) h.Authorization = `Bearer ${anorToken}`;
    return h;
  }, [anorToken]);

  // cerrar modal con Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setAnorDetailOpen(false);
    };
    if (anorDetailOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [anorDetailOpen]);

  const anorCanSearch = useMemo(() => {
    if (anorMode === "number") return anorOrderNumber.trim().length > 0;
    return !!anorDateFrom || !!anorDateTo;
  }, [anorMode, anorOrderNumber, anorDateFrom, anorDateTo]);

  const anorBuildSearchUrl = () => {
    const params = new URLSearchParams();
    if (anorMode === "number") params.set("orderNumber", anorOrderNumber.trim());
    if (anorMode === "date") {
      if (anorDateFrom) params.set("dateFrom", anorDateFrom);
      if (anorDateTo) params.set("dateTo", anorDateTo);
    }
    return `${API_BASE}/api/admin/orders/search?${params.toString()}`;
  };

  const anorSearchOrders = async () => {
    if (!anorCanSearch) {
      setAnorMsg({ type: "err", text: "Debes ingresar un # de orden o fechas para buscar." });
      return;
    }

    setAnorMsg(null);
    setAnorLoading(true);
    setAnorOrders([]);

    try {
      const res = await fetch(anorBuildSearchUrl(), { method: "GET", headers: anorHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error buscando órdenes.");

      const list = data?.orders || [];
      setAnorOrders(list);

      if (list.length === 0) {
        setAnorMsg({ type: "err", text: "No se encontraron órdenes con ese criterio." });
      }
    } catch (e) {
      setAnorMsg({ type: "err", text: e.message || "Error buscando órdenes." });
    } finally {
      setAnorLoading(false);
    }
  };

  const anorOpenDetail = async (orderId) => {
    setAnorSelectedId(orderId);
    setAnorSelectedOrder(null);
    setAnorDetailOpen(true);
    setAnorDetailLoading(true);
    setAnorMsg(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
        method: "GET",
        headers: anorHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error obteniendo detalle de la orden.");

      const order = data?.order ? data.order : null;
        setAnorSelectedOrder(order);
    } catch (e) {
      setAnorMsg({ type: "err", text: e.message || "Error obteniendo detalle." });
      setAnorDetailOpen(false);
    } finally {
      setAnorDetailLoading(false);
    }
  };

  const anorAnnulSelected = async () => {
    if (!anorSelectedId) return;

    const ok = window.confirm(
      "⚠️ ¿Confirmas ANULAR esta orden?\n\nEsto la eliminará por completo del sistema."
    );
    if (!ok) return;

    setAnorAnnulLoading(true);
    setAnorMsg(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/orders/${anorSelectedId}/annul`, {
        method: "POST",
        headers: anorHeaders,
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error anulando la orden.");

      setAnorOrders((prev) => prev.filter((o) => String(o.id) !== String(anorSelectedId)));
      setAnorMsg({ type: "ok", text: data?.message || "Orden anulada correctamente." });

      setAnorDetailOpen(false);
      setAnorSelectedId(null);
      setAnorSelectedOrder(null);
    } catch (e) {
      setAnorMsg({ type: "err", text: e.message || "Error anulando la orden." });
    } finally {
      setAnorAnnulLoading(false);
    }
  };

  return (
    <div className="anor-wrap">
      <header className="anor-head">
        <h1 className="anor-title">Anular Órdenes</h1>
        <p className="anor-subtitle">Busca una orden, revisa el detalle completo y anúlala si es necesario</p>
      </header>

      <section className="anor-card">
        <div className="anor-tabs">
          <button
            className={anorMode === "number" ? "anor-tab anor-tab--active" : "anor-tab"}
            onClick={() => setAnorMode("number")}
            type="button"
          >
            Buscar por # Orden
          </button>
          <button
            className={anorMode === "date" ? "anor-tab anor-tab--active" : "anor-tab"}
            onClick={() => setAnorMode("date")}
            type="button"
          >
            Buscar por Fecha
          </button>
        </div>

        {anorMode === "number" ? (
          <div className="anor-form anor-form--number">
            <div className="anor-field">
              <label className="anor-label">Número de Orden</label>
              <input
                className="anor-input"
                type="text"
                value={anorOrderNumber}
                onChange={(e) => setAnorOrderNumber(e.target.value)}
                placeholder="Ej: OR-2425 / F2000-501 / 501"
              />
            </div>

            <div className="anor-actions">
              <button
                className="anor-btn anor-btn--primary"
                type="button"
                onClick={anorSearchOrders}
                disabled={anorLoading || !anorCanSearch}
              >
                {anorLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </div>
        ) : (
          <div className="anor-form anor-form--date">
            <div className="anor-field">
              <label className="anor-label">Desde</label>
              <input
                className="anor-input"
                type="date"
                value={anorDateFrom}
                onChange={(e) => setAnorDateFrom(e.target.value)}
              />
            </div>

            <div className="anor-field">
              <label className="anor-label">Hasta</label>
              <input
                className="anor-input"
                type="date"
                value={anorDateTo}
                onChange={(e) => setAnorDateTo(e.target.value)}
              />
            </div>

            <div className="anor-actions">
              <button
                className="anor-btn anor-btn--primary"
                type="button"
                onClick={anorSearchOrders}
                disabled={anorLoading || !anorCanSearch}
              >
                {anorLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </div>
        )}

        {anorMsg?.text && (
          <div className={anorMsg.type === "ok" ? "anor-msg anor-msg--ok" : "anor-msg anor-msg--err"}>
            {anorMsg.text}
          </div>
        )}
      </section>

      <section className="anor-results">
        <h2 className="anor-h2">Resultados</h2>

        <div className="anor-tableWrap">
          <table className="anor-table">
            <thead>
              <tr>
                <th># Orden</th>
                <th>Cliente</th>
                <th>Forma Pago</th>
                <th>Total</th>
                <th>Creada</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {anorOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="anor-empty">
                    {anorLoading ? "Cargando..." : "Sin resultados"}
                  </td>
                </tr>
              ) : (
                anorOrders.map((o) => (
                  <tr key={o.id}>
                    <td>{o.orderNumber || "-"}</td>
                    <td>{o.customer || "-"}</td>
                    <td>{o.formaPago || "-"}</td>
                    <td>{anorFormatCRC(o.totalPayment)}</td>
                    <td>{anorFormatDateTime(o.createdAt)}</td>
                    <td>
                      <button className="anor-btn anor-btn--ghost" type="button" onClick={() => anorOpenDetail(o.id)}>
                        Ver detalle
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== Modal Detalle ===== */}
      {anorDetailOpen && (
        <div className="anor-modalOverlay" onClick={() => setAnorDetailOpen(false)}>
          <div className="anor-modalCard" onClick={(e) => e.stopPropagation()}>
            <button className="anor-modalClose" onClick={() => setAnorDetailOpen(false)} aria-label="Cerrar">
              ×
            </button>

            <div className="anor-modalHead">
              <h2 className="anor-modalTitle">DETALLE DE ORDEN</h2>
              <p className="anor-modalSub">Revisa toda la información antes de anular</p>
            </div>

            <div className="anor-sep" />

            {anorDetailLoading ? (
              <div className="anor-loading">Cargando detalle...</div>
            ) : !anorSelectedOrder ? (
              <div className="anor-loading">No se pudo cargar la orden.</div>
            ) : (
              <>
                <div className="anor-grid">
                  <div className="anor-box">
                    <span className="anor-k"># Orden</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.orderNumber, "-")}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Creada</span>
                    <span className="anor-v">{anorFormatDateTime(anorSelectedOrder.createdAt)}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Cliente (texto)</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.customer, "-")}</span>
                  </div>

                  <div className="anor-box">
  <span className="anor-k">clientId (resuelto)</span>
  <span className="anor-v">
    {anorSafeText(
      anorSelectedOrder?.clientIdResolved || anorSelectedOrder?.clientId || 0,
      "-"
    )}
  </span>
</div>


                  <div className="anor-box">
                    <span className="anor-k">Forma pago</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.formaPago, "-")}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Estado</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.status, "-")}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Total</span>
                    <span className="anor-v">{anorFormatCRC(anorSelectedOrder.totalPayment)}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Recibido Por</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.recibidoPor, "-")}</span>
                  </div>

                  <div className="anor-box">
                    <span className="anor-k">Fact</span>
                    <span className="anor-v">{anorSafeText(anorSelectedOrder.fact, "N/A")}</span>
                  </div>
                </div>

                <div className="anor-block">
                  <h3 className="anor-h3">Productos</h3>
                  <div className="anor-text">
                    {anorSafeText(anorSelectedOrder.detalleEntrega, "Sin productos.")}
                  </div>
                </div>

                <div className="anor-block">
                  <h3 className="anor-h3">Tarjeta</h3>
                  <div className="anor-text">
                    {anorSafeText(anorSelectedOrder.tarjetaDetalle, "N/A")}
                  </div>
                </div>

                <div className="anor-modalActions">
                  <button
                    className="anor-btn anor-btn--danger"
                    type="button"
                    onClick={anorAnnulSelected}
                    disabled={anorAnnulLoading}
                  >
                    {anorAnnulLoading ? "Anulando..." : "ANULAR ORDEN"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnularOrdenes;
